/**
 * The boot splash: the smile from the wordmark draws itself, turns into its
 * place, the word unfolds around it and catches a shine.
 *
 * Played for someone signed in, on load or right after signing in. It sits
 * over the app rather than in place of it, so the app mounts and starts
 * fetching underneath while the animation plays, and it always plays to the
 * end. On a desktop with the sidebar open the finished logo then flies onto
 * the sidebar's own wordmark and the page fades in around it; where that logo
 * is not on screen (a phone, a collapsed sidebar) the splash simply fades.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SCANDI_PATHS, ScandiGradient, useSvgId } from "@/components/brand/scandi-wordmark";
import "./scandi-loader.css";

/** When the shine in scandi-loader.css finishes. */
const INTRO_MS = 1850;
/** Longest the hand-over may take before the splash is simply removed. */
const HANDOVER_MAX_MS = 3000;

/** The element the logo lands on. */
export const BRAND_TARGET_ATTR = "data-brand-target";

const LETTERS = [
  { d: SCANDI_PATHS.s, cls: "sl-s" },
  { d: SCANDI_PATHS.a, cls: "sl-a" },
  { d: SCANDI_PATHS.n, cls: "sl-n" },
  { d: SCANDI_PATHS.d, cls: "sl-d" },
  { d: SCANDI_PATHS.i, cls: "sl-i" },
];

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function BootSplash({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const backdropRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<SVGSVGElement>(null);
  const [introDone, setIntroDone] = useState(prefersReducedMotion);
  // Kept in a ref so a re-render of the parent mid-flight does not restart it.
  const onDoneRef = useRef(onDone);
  useEffect(() => {
    onDoneRef.current = onDone;
  });

  useEffect(() => {
    if (introDone) return;
    const id = window.setTimeout(() => setIntroDone(true), INTRO_MS);
    return () => window.clearTimeout(id);
  }, [introDone]);

  useEffect(() => {
    if (!introDone) return;
    // Two frames so the app underneath has mounted and laid out the sidebar.
    // Once the hand-over has started it runs to the end.
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        // Whatever happens in the hand-over, the app must come up: a splash that
        // never leaves is far worse than one that leaves without its flourish.
        const giveUp = new Promise<void>((resolve) => window.setTimeout(resolve, HANDOVER_MAX_MS));
        void Promise.race([handOver(logoRef.current, backdropRef.current), giveUp])
          .catch((error: unknown) => console.error("Boot splash hand-over failed", error))
          .finally(() => onDoneRef.current());
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [introDone]);

  return (
    <div role="status" aria-label={t("common.loading")} className="fixed inset-0 z-[100] grid place-items-center">
      <div ref={backdropRef} className="bg-background absolute inset-0" />
      <ScandiLoaderMark ref={logoRef} className="relative w-[min(560px,78vw)] origin-top-left" />
    </div>
  );
}

/**
 * Lands the finished logo and lifts the backdrop. Resolves when the app is
 * fully revealed.
 */
async function handOver(logo: SVGSVGElement | null, backdrop: HTMLDivElement | null) {
  if (!logo || !backdrop) return;
  const fade = (el: Element, duration: number) =>
    el.animate([{ opacity: 1 }, { opacity: 0 }], { duration, easing: "ease-out", fill: "forwards" })
      .finished;

  if (prefersReducedMotion()) {
    await Promise.all([fade(backdrop, 200), fade(logo, 200)]);
    return;
  }

  // The header's position depends on the font and the sidebar having laid
  // out; measure only once they have.
  await Promise.race([document.fonts.ready, new Promise((r) => window.setTimeout(r, 300))]);
  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  const target = document.querySelector<SVGElement>(`[${BRAND_TARGET_ATTR}]`);
  const to = target?.getBoundingClientRect();
  const desktop = window.matchMedia("(min-width: 768px)").matches;

  if (!target || !to || !desktop || to.width === 0 || to.height === 0) {
    await Promise.all([
      fade(backdrop, 450),
      logo.animate(
        [
          { transformOrigin: "center", transform: "scale(1)", opacity: 1 },
          { transformOrigin: "center", transform: "scale(1.04)", opacity: 0 },
        ],
        { duration: 280, easing: "ease-out", fill: "forwards" },
      ).finished,
    ]);
    return;
  }

  // Both are the same viewBox at the same aspect ratio, so matching the boxes
  // matches every glyph. The real one stays hidden until the overlay is gone,
  // so the two never double up on the edges.
  const from = logo.getBoundingClientRect();
  target.style.opacity = "0";
  logo.style.willChange = "transform";
  const FLIGHT_MS = 700;
  const flight = logo.animate(
    [
      { transform: "translate(0px, 0px) scale(1)" },
      {
        transform: `translate(${to.left - from.left}px, ${to.top - from.top}px) scale(${to.width / from.width})`,
      },
    ],
    { duration: FLIGHT_MS, easing: "cubic-bezier(0.65, 0, 0.2, 1)", fill: "forwards" },
  ).finished;
  // The page starts to come up as the logo settles into the header, rather
  // than after it has stopped dead, so the two read as one motion.
  const reveal = backdrop.animate([{ opacity: 1 }, { opacity: 0 }], {
    duration: 400,
    delay: FLIGHT_MS * 0.7,
    easing: "cubic-bezier(0.33, 0, 0.2, 1)",
    fill: "forwards",
  }).finished;
  await Promise.all([flight, reveal]);
  target.style.opacity = "";
}

/** The animated logo itself; plays once on mount. */
function ScandiLoaderMark({
  className,
  ref,
}: {
  className?: string;
  ref?: React.Ref<SVGSVGElement>;
}) {
  const gradientId = useSvgId("sl-gradient");
  const mouthId = useSvgId("sl-mouth");
  const shineMaskId = useSvgId("sl-shine-mask");
  const shineId = useSvgId("sl-shine");

  return (
    <svg
      ref={ref}
      viewBox="0 0 5800 1707"
      aria-hidden
      className={`scandi-loader ${className ?? ""}`}
      fill={`url(#${gradientId})`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <ScandiGradient id={gradientId} />
        {/* An arc over the c, swept from one corner of the mouth to the other. */}
        <mask id={mouthId} maskUnits="userSpaceOnUse" x="0" y="0" width="5800" height="1707">
          <path
            className="sl-mouth-draw"
            pathLength={1}
            fill="none"
            stroke="#fff"
            strokeWidth={270}
            d="M1720 640 L1606 640 A390 390 0 0 0 1606 1420 L1720 1420"
          />
        </mask>
        {/* The whole word at rest, for the shine to be clipped to. */}
        <mask id={shineMaskId} maskUnits="userSpaceOnUse" x="0" y="0" width="5800" height="1707">
          {Object.values(SCANDI_PATHS).map((d) => (
            <path key={d} d={d} fill="#fff" />
          ))}
        </mask>
        <linearGradient id={shineId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#fff" stopOpacity="0" />
          <stop offset="0.5" stopColor="#fff" stopOpacity="0.85" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>

      {LETTERS.map(({ d, cls }) => (
        <path key={cls} className={`sl-letter ${cls}`} d={d} />
      ))}
      <path className="sl-tittle" d={SCANDI_PATHS.iDot} />

      <g className="sl-smile">
        <path mask={`url(#${mouthId})`} d={SCANDI_PATHS.c} />
        <g className="sl-eye sl-eye-top">
          <path className="sl-blink" d={SCANDI_PATHS.colonTop} />
        </g>
        <g className="sl-eye sl-eye-bottom">
          <path className="sl-blink" d={SCANDI_PATHS.colonBottom} />
        </g>
      </g>

      <g mask={`url(#${shineMaskId})`}>
        <g transform="skewX(-20)">
          <rect className="sl-shine" x="-1400" y="0" width="1100" height="1707" fill={`url(#${shineId})`} />
        </g>
      </g>
    </svg>
  );
}
